// Event categories, and the reminder lead time that hangs off one.
//
// Domain rather than src/data/useCategories.jsx because the *scheduled
// reminder worker* needs the same list and the same reading of a category's
// lead time as the browser does, and functions/ only sees src/domain
// (functions/scripts/sync-domain.mjs copies it).
//
// ── What MMS does (#87) ─────────────────────────────────────────────────────
//
// The lead time is a **per-category** control, not a studio setting and not a
// per-student one:
//
//   Calendar → Options → Categories & Locations → a category's ⋮ → Edit
//   Category. The drawer carries **Email Reminders** and **SMS Reminders**
//   side by side, each a dropdown:
//
//     No Reminders · 1 hour before · 2 hours before · 3 hours before ·
//     1 day before · 2 days before · Custom…
//
//   `Custom…` replaces the dropdown with a number field whose unit is fixed to
//   **hours before** — static text, not a second dropdown. So every lead MMS
//   can express is a whole number of hours, which is why the stored unit here
//   is hours and not days.
//
// **Every one of the six stock categories ships `No Reminders`**, so
// `reminderLeadHours` is absent from the defaults below: a studio that has
// never opened this menu sends no lesson reminders, whatever its students have
// ticked. MMS says so on the student record — "This student will only receive
// reminders for categories with reminders enabled". See REMINDER_TIMING in
// domain/reminders.js.

// `./core.js`, not `../lib/core.js`: this file is copied verbatim into
// functions/domain/, and that door is inside the copy (#218).
import { coreSync } from './core.js';
import { documentFromRows } from './documents.js';

// The lead, and the one value that is not a number.
//
// `null` is "No Reminders" and is a different thing from `0`. Zero would be a
// lead of zero hours — remind at the moment the lesson starts — which MMS's
// dropdown cannot express. Absent reads as null, so a category document without
// the field is off, as MMS's defaults are.
export const NO_REMINDERS = null;

// The stock dropdown, in MMS's order. `Custom…` is not in here: it is a mode
// the editor switches into, not a value, and what it produces is one of these
// numbers or one MMS has no label for.
export const REMINDER_LEAD_OPTIONS = Object.freeze([
  Object.freeze({ hours: NO_REMINDERS, label: 'No Reminders' }),
  Object.freeze({ hours: 1, label: '1 hour before' }),
  Object.freeze({ hours: 2, label: '2 hours before' }),
  Object.freeze({ hours: 3, label: '3 hours before' }),
  Object.freeze({ hours: 24, label: '1 day before' }),
  Object.freeze({ hours: 48, label: '2 days before' }),
]);

// The ceiling on a custom lead. **1000, which is MMS's number** (#103).
//
// ── What MMS enforces ──────────────────────────────────────────────────────
//
// 1 to 1000 hours on the Custom field, validated on Save, with these two
// messages verbatim:
//
//     Please enter an amount less than or equal to 1000
//     ...greater than or equal to 1
//
// (41 days and 16 hours).
//
// **The editor refuses too (#131)**, as MMS refuses to Save: `customLeadError`
// below refuses *before* the write. `normaliseLeadHours` still clamps, because
// the server reads this field and must bound whatever a client sent.
//
// ── The two things that depend on this ─────────────────────────────────────
//
//  1. **The sweep's calendar window.** `dueReminders` opens a window of
//     `ceil(maxLead / 24)` days — 42 at the ceiling — and expands every series
//     across it. The lead is client-written, so without a ceiling a studio
//     could ask the hourly worker to expand centuries of recurrence. A studio
//     is read at `READ_CAP` = 2,000 series or skipped whole, and one series
//     yields at most one occurrence a day, so the worst case is 84,000
//     occurrences considered per studio; writes stay at `DUE_CAP` = 200 per
//     studio per run. The window is sized by the largest lead the studio's
//     categories hold, so the stock No Reminders costs nothing.
//
//  2. **Retention.** A lesson reminder's idempotence *is* its message document
//     (functions/retention/sweep.js): once the document is swept, the id can be
//     re-derived and the family gets a second copy. The message is created at
//     most `maxLead` before the lesson and stops being re-derivable once the
//     lesson is past, so `maxLead` is the whole exposure: 1000 hours is
//     **41.7 days**, against `RETENTION.messageDays` = 90.
//
// Raising this past `RETENTION.messageDays` would re-open the double send.
// Both numbers are named in functions/README.md for that reason, and
// test/functions/retention.test.js asserts the inequality.
export const MAX_REMINDER_LEAD_HOURS = 1000;

// The stock categories.
//
// Offered rather than written at signup — see useCategories.jsx, which
// materialises the whole set the first time a studio saves any one of them.
// Nothing here carries `reminderLeadHours`, and that absence is the MMS
// default: No Reminders on all six.
//
// **The order of this array carries no meaning** (#111). `sortCategories`
// below is the one order, applied to this array and to a stored list alike,
// so nothing moves when the list first comes back from Firestore. Anything
// that needs a *particular* category names its id.
//
// ── `allowMakeupCredits` ───────────────────────────────────────────────────
//
// MMS's own field (#351): `Calendar → Options ▾ → Categories & Locations → ⋮ →
// Edit Category` holds a **Make-Up Credits** section with one checkbox, `Allow
// make-up credits to be used for this event type`, shown in the list as a
// `Make-Up Credit` column. It is what `statuses_for` in
// `core/src/attendance.rs` calls `allows_makeup_credits`.
//
// **A new category defaults off**, in MMS and in `emptyDraft` in
// CategoriesDrawer.jsx. MMS ships its own six with two of them on; these are
// our six:
//
//   ours            MMS's counterpart        MMS       taken here
//   ───────────────────────────────────────────────────────────────
//   Lesson          Lesson                   on        true
//   Group Class     Group Lesson             on        true
//   Recital         Recital                  off       false
//   Unavailable     Vacation                 off       false
//   Make-Up Lesson  — none —                 —         true   ← ours (#361)
//   Trial Lesson    — none —                 —         false  ← ours (#361)
//
// **The last two are ours.** MMS's stock six are Birthday, Group Lesson,
// Lesson, Recital, Repertoire Performance and Vacation; it has no Make-Up
// Lesson and no Trial Lesson.
//
// **Make-Up Lesson is `true`** because `statuses_for` gates on this field: with
// it off, a lesson in the category is offered no `Present, Use Make-Up Credit`
// status. This is a seed, not a read; the read path below still treats an
// unreadable value as off.
//
// **Trial Lesson stays `false`**: a trial is a prospect's single lesson outside
// any paid schedule, with no credit relationship to enable.
//
// `saveCategory` materialises this array on a studio's *first* write, so a
// default here reaches only studios that have never saved a category.
//
// `test/domain/categories.test.js` states each of them as ours rather than as
// an MMS reading (#286).
export const DEFAULT_CATEGORIES = Object.freeze([
  { id: 'lesson', label: 'Lesson', color: '#38bdf8', billable: true, allowMakeupCredits: true },
  // `true` is ours and is argued above (#361): with it off, this category
  // offers no `Use Make-Up Credit` status at all.
  { id: 'makeup', label: 'Make-Up Lesson', color: '#34d399', billable: true, allowMakeupCredits: true },
  { id: 'trial', label: 'Trial Lesson', color: '#fbbf24', billable: true, allowMakeupCredits: false },
  { id: 'group', label: 'Group Class', color: '#a78bfa', billable: true, allowMakeupCredits: true },
  { id: 'recital', label: 'Recital', color: '#f472b6', billable: false, allowMakeupCredits: false },
  { id: 'unavailable', label: 'Unavailable', color: '#9ca3af', billable: false, allowMakeupCredits: false },
].map((c) => Object.freeze(c)));

// Whether a category's lessons bill. **On unless it says `false`**: a category
// without `billable` bills (CategoryList.jsx, CategoryFields.jsx).
export function categoryIsBillable(category) {
  const b = category?.billable;
  return coreSync().categoryIsBillable(typeof b === 'boolean' ? b : null);
}

// Does this category allow make-up credits to be issued or spent on its events?
//
// **The read path: anything that is not a stored `true` is off** — a category
// without the field, one holding something that is not a boolean, and an event
// whose `categoryId` names no category the studio holds (an event carrying only
// a bare `categoryLabel`, #110's shape). Off for `normaliseLeadHours`'s reason
// below: a value we cannot read falls silent rather than offering to grant or
// spend a credit on a recital. An event not yet linked to a category is offered
// six statuses rather than eight until the backfill links it.
export function allowsMakeupCredits(category) {
  const a = category?.allowMakeupCredits;
  return coreSync().categoryAllowsMakeupCredits(typeof a === 'boolean' ? a : null);
}

// The colours a managed list offers.
//
// Here rather than in components/calendar/categories/CategoryFields.jsx
// because the **tag** editor offers the same eight (#138): MMS draws the tag
// colour picker from the same palette as the category one — the swatch
// dropdown with the named colours, `Beach` and the rest.
//
// CategoryFields re-exports it, so call sites may import it from there.
export const SWATCHES = Object.freeze([
  '#38bdf8', '#34d399', '#fbbf24', '#a78bfa',
  '#f472b6', '#fb7185', '#9ca3af', '#4ade80',
]);

// The id of the category a form starts on, absent any other instruction.
//
// Named rather than positional (#141), so re-ordering a list cannot change
// what the app defaults to.
export const DEFAULT_CATEGORY_ID = 'lesson';

// The one order a category list is ever shown in: **alphabetical by name,
// always** (#111).
//
// That is MMS's: `Calendar → Options ▾ → Categories & Locations → Categories`
// lists them alphabetically, a rename moves a row immediately, and there is no
// drag handle, position column or custom order, so there is no stored order.
//
// Applied to `DEFAULT_CATEGORIES` and to a studio's stored list alike, so the
// first write to the collection does not re-sort the drawer.
//
// The tie-break on id: two categories can share a name — the editor refuses it
// (#129) but two offline devices can still get there (#142) — and a snapshot's
// order is not a promise, so two devices show the same order only with it.
//
// The order is the core's (`sort_categories`, on `js_compat::locale_compare`),
// so the phones list them identically. That approximates ICU's root
// collation; the one recorded answer it gives differently — `Straße` against
// `STRASSE` — is named in `core/src/calendar_recorded.rs`.
export function sortCategories(categories = []) {
  const list = [...(categories || [])];
  const refs = list.map((c) => ({ id: String(c?.id ?? ''), label: String(c?.label ?? '') }));
  return coreSync().sortCategories(refs).map((i) => list[i]);
}

// Categories as the core's rules read them: an id, and a label only when it
// is a string (`categoryKey` of anything else is no key).
const refsOf = (categories = []) => (categories || []).map((c) => ({
  id: c?.id == null ? null : String(c.id),
  label: typeof c?.label === 'string' ? c.label : null,
}));

const textOrNull = (v) => (v == null ? null : String(v));

// Which category a brand-new event — or a brand-new student — opens on.
//
// MMS's New Calendar Event opens with Category reading **Lesson**
// (research/mms-capture/03-modals/cal-02-new-event-top.jpg, SPEC.md §2.1), and
// so does Step 3 of its add-student wizard
// (03-modals/students-06-wizard-s3-lesson-pricing.jpg, SPEC.md §1.2), in a
// populated studio too, so MMS means Lesson rather than whatever sorts first.
//
// Found by **id**, not by name (#128) and not by position (#141): the id
// survives the studio renaming the category to "Piano Lesson". Falling back to
// the first category is for the studio that deleted the built-in; falling back
// to null is for the frame before the list arrives.
export function defaultEventCategory(categories = []) {
  const at = coreSync().defaultEventCategory(refsOf(categories));
  return at == null ? null : categories[at];
}

// One stored lead, read safely.
//
// Anything that is not a whole number of hours in range is `No Reminders`: a
// lead we cannot read falls silent rather than sending mail nobody asked for.
// A value above the ceiling is clamped, since the intent is "as far ahead as
// you allow".
//
// **This is the read path and nothing else** (#131): it makes a stored number
// safe to hand the reminder sweep. The editor refuses out of range before it
// writes (`customLeadError` below).
export function normaliseLeadHours(value) {
  return coreSync().normaliseLeadHours(textOrNull(value)) ?? NO_REMINDERS;
}

// How far ahead this category is reminded about on this channel, or null.
//
// Two channels because MMS asks twice — Email Reminders and SMS Reminders are
// separate dropdowns with the same options, and a studio that wants an email
// two days out and a text an hour out can say so. Stored as a map keyed by
// `CHANNELS` (domain/notifications.js) rather than two flat fields so the
// worker's `for (const channel of t.channels)` loop reads one lookup.
export function categoryLeadHours(category, channel) {
  const map = category?.reminderLeadHours;
  if (!map || typeof map !== 'object') return NO_REMINDERS;
  return normaliseLeadHours(map[channel]);
}

// Whether this category sends anything at all, on any channel. Used by the
// editor to label a row, and nowhere in the decision — the decision asks per
// channel, because that is how the teacher set it.
export function categorySendsReminders(category, channels = ['email', 'sms']) {
  return channels.some((c) => categoryLeadHours(category, c) !== NO_REMINDERS);
}

// The map to store, from whatever the editor is holding. Always both keys and
// always explicit, so setting a channel back to "No Reminders" writes a null
// rather than leaving the previous number behind a `{ merge: true }` write.
export function leadHoursMap({ email = NO_REMINDERS, sms = NO_REMINDERS } = {}) {
  return { email: normaliseLeadHours(email), sms: normaliseLeadHours(sms) };
}

// ── The documents the Categories & Locations drawer writes ─────────────────
//
// What `saveCategory`, `removeCategory`, `saveLocation`, `applyCategoryMerge`
// and `relabelCategory` write, from `core/src/categories.rs`, so the phones
// write the same. `serverTimestamp` is the writer's sentinel.

// A category as the editor saves it, merged onto its id.
//
// `billable` is false only when said to be. Make-up credits (#351 — MMS's
// `Allow make-up credits to be used for this event type`, and the outer gate
// at `statuses_for` in `core/src/attendance.rs`) are allowed only when said to
// be: `=== true` rather than `!== false` is the whole difference between the
// two fields, since a category that says nothing about charges is billable and
// one that says nothing about make-up credits is off. Both are always written,
// never omitted, and the reminder leads (#87) are written whole, both keys and
// a null for "No Reminders": on a `{ merge: true }` write, a key left out
// would keep its old value.
export function categoryDocument({
  label, color, billable, allowMakeupCredits, reminderLeadHours,
} = {}, serverTimestamp = undefined) {
  const leads = reminderLeadHours || {};
  return documentFromRows(coreSync().categoryDocument(
    label ? String(label) : null,
    color ? String(color) : null,
    billable === false ? false : null,
    allowMakeupCredits === true ? true : null,
    textOrNull(leads.email),
    textOrNull(leads.sms),
  ), serverTimestamp);
}

// One built-in category as it is materialised beside a studio's first write
// to the list — every field of the default, its `id` included, and
// `createdAt`. A whole document, not a merge.
export function defaultCategoryDocument(category, serverTimestamp = undefined) {
  return documentFromRows(coreSync().defaultCategoryDocument(String(category?.id ?? '')), serverTimestamp);
}

// A location, merged onto its id.
export function locationDocument({ label } = {}, serverTimestamp = undefined) {
  return documentFromRows(coreSync().locationDocument(label ? String(label) : null), serverTimestamp);
}

// What re-pointing a document at a category writes — a merge's rows (#142):
// both halves of the pair, and `updatedAt`.
export function categoryLinkDocument(categoryId, categoryLabel, serverTimestamp = undefined) {
  return documentFromRows(
    coreSync().categoryLinkDocument(String(categoryId), String(categoryLabel ?? '')),
    serverTimestamp,
  );
}

// What a rename carries onto each event filed under the category
// (`relabelCategory`): the new label, and `updatedAt`.
export function categoryRelabelDocument(label, serverTimestamp = undefined) {
  return documentFromRows(coreSync().categoryRelabelDocument(String(label)), serverTimestamp);
}

// One lead in MMS's own words, for a surface that shows it rather than edits
// it — the category row's summary line (#98).
//
// The stock options answer for themselves; a custom number borrows their
// grammar ("5 hours before"), which is the phrasing MMS's own custom field
// spells out beside the box. "No Reminders" for the null, capitalised as MMS
// capitalises it in the dropdown.
export function leadLabel(hours) {
  return coreSync().leadLabel(textOrNull(hours));
}

// Whether a lead is one of the stock options or a `Custom…` number. The editor
// needs the question to decide which control to show; MMS makes the same
// distinction, and a studio that typed 24 into Custom is shown "1 day before"
// on its next visit because that is the option that means it.
export function isStockLead(hours) {
  return coreSync().isStockLead(textOrNull(hours));
}

// ─── The Custom box, refused rather than clamped (#131) ─────────────────────
//
// MMS validates its Custom lead **on Save** and refuses out of range, with
// these two sentences (#103). The first is MMS's verbatim; the second's
// opening is reconstructed from the first, since only "...greater than or
// equal to 1" was recorded.
export const LEAD_TOO_HIGH = `Please enter an amount less than or equal to ${MAX_REMINDER_LEAD_HOURS}`;
export const LEAD_TOO_LOW = 'Please enter an amount greater than or equal to 1';

// Whether the editor should be showing the number box rather than the dropdown.
//
// **Not `!isStockLead(...)`**, which normalises first: 5000 comes back as 1000
// and 0 as `No Reminders`, a stock option, so the two values this control most
// needs to keep visible would be hidden. This reads the value as typed.
export function isCustomLead(value) {
  return coreSync().isCustomLead(textOrNull(value));
}

// What MMS refuses about a typed lead, or null.
//
// The clamp in `normaliseLeadHours` is the read path, and `firestore.rules`
// bounds the field again; this is the refusal *before* the write, so a teacher
// who typed 5000 is told rather than silently given 1000 (#93's complaint, in
// a different field).
//
// An **empty** box is not an error and means No Reminders, as the Cancel
// beside it does. Whether MMS refuses an empty Custom field is not known.
//
// An unreadable value is not refused either: `type="number"` hands back an
// empty string for anything it cannot parse. The rule is
// `categories::custom_lead_error` in the core, and its two sentences the
// core's message table; the constants above are pinned to it.
export function customLeadError(value) {
  return coreSync().customLeadError(textOrNull(value))?.message ?? null;
}

// ─── When two labels are the same label ─────────────────────────────────────
//
// One key, shared by the editor's duplicate-name refusal (#129,
// `categoryErrors` below) and #110's one-off backfill (`planCategoryBackfill`,
// further down). The backfill **refuses** — writes nothing, picks neither —
// when two categories answer to the same key, so the names the editor lets a
// studio create are exactly the names the backfill can repair. Loosening or
// tightening the key on one side only would break that; **change them
// together.**
//
// The other callers ask the same question of other collections:
//
//   * `expenseCategoryErrors` in domain/expenses.js (#144) — the same refusal
//     on Expenses & Other Revenue → Categories, scoped to the entry type
//     because the stock list ships an expense "Other" and a revenue "Other".
//   * `resolveImportCategory` in domain/importDelimited.js (#134), matching a
//     spreadsheet's Category cell to an expense category. Its `ambiguous`
//     verdict is reachable only by two offline devices that could not see each
//     other's write, or by a studio already in that state (#144, #142).
//   * `tagKey` in domain/tags.js (#138), for a student's instrument and group
//     tags.

// The key two labels are compared on.
//
// Trimmed, internal runs of whitespace collapsed to one space, and lowercased.
// Unicode-normalised first, so a composed "é" and a decomposed one are one
// category rather than two.
//
// **Case- and whitespace-insensitive**, so a migration matches the rows where
// somebody held shift or hit the space bar twice. "Lessson" is still not
// "Lesson"; a typo is a different word.
//
// Read as the editor's refusal: **"Lesson" and "lesson" are one name** (case), so are
// `" Lesson "` and `"Lesson"` (surrounding whitespace) and `"Group  Class"` and
// `"Group Class"` (internal runs), and so are a composed "é" and a decomposed
// one (NFC). **"Lessson" is a different name** and may be created alongside
// "Lesson"; refusing it would be a spell-checker, and a teacher who wants
// "Theory" and "Theory (Group)" must be able to have both.
//
// Three of those four are MMS's too. The internal-runs one is not: MMS accepts
// "Group  Lesson" beside "Group Lesson" (see `categoryErrors`).
//
// Null for anything that is not a usable label, so an empty string and a
// missing field are the same non-answer rather than a key that collides.
//
// **The key is `tags::category_key` in the core** — NFC, JavaScript's trim,
// runs of JavaScript's white space folded to one space, `toLowerCase` — so the
// phones' tag and category pickers call a name "already there" exactly when
// this one does. `core/src/people_forms_vectors.json` holds its recorded
// answers, final sigma and dotted I included.
export function categoryKey(label) {
  if (typeof label !== 'string') return null;
  return coreSync().categoryKey(label) ?? null;
}

// The category this draft's name collides with, or null.
//
// `selfId` is the draft's own id when one is being edited, so renaming a
// category to the name it already has is not a collision with itself. Absent on
// the Add block, where there is no self yet.
export function clashingCategory(label, selfId, categories = []) {
  if (typeof label !== 'string') return null;
  const at = coreSync().clashingCategory(label, textOrNull(selfId), refsOf(categories));
  return at == null ? null : categories[at];
}

// Our wording, but MMS's shape and MMS's trigger — see `categoryErrors`. It
// names the *existing* category's own spelling, so a clash of case or spacing
// reads right: a teacher typing "lesson" is told there is already a "Lesson".
export function duplicateLabelMessage(existingLabel) {
  return coreSync().duplicateCategoryMessage(String(existingLabel));
}

// What Add Category and Edit Category refuse, keyed by the field it belongs
// under. The shape `entryErrors` in hooks/useExpenseEntryForm.js has, and for
// the same reason: exported pure so the rule can be exercised without a drawer.
//
// ── Where uniqueness is enforced, and why here ─────────────────────────────
//
// #129. Uniqueness is enforced in the editor, not elsewhere:
//
//   **firestore.rules cannot.** A rule may `get()` a document it can *name*;
//   it cannot ask whether any other document has a given field value, so it
//   could only enforce uniqueness through the document id.
//
//   **A deterministic id** derived from the label would not survive the label
//   being *mutable*: `relabelCategory` matches events on `categoryId`, so the
//   id must not move on a rename, and a derived id then goes stale. It would
//   not cover the built-ins either — `DEFAULT_CATEGORIES` ship ids `lesson`,
//   `makeup`, `group` …, which no derivation from "Make-Up Lesson" or "Group
//   Class" produces.
//
//   **The editor** can say *why* to the person who typed the name, and needs
//   no read: `useCategories` holds the whole collection in a live snapshot,
//   served from cache offline.
//
// Two devices, both offline, both adding "Lesson", still converge on two
// documents. The backfill refuses an ambiguous key and repairs every other
// one, so that case is a repair skipped and reported, not a wrong category
// chosen.
//
// ── #142: the server-side answers, modelled and not used ───────────────────
//
// Both are executable in test/domain/categoryConvergentIds.test.js.
//
// **The deterministic id actively breaks a case**, online on one device:
// rename "Lesson" to "Piano", then add a category called "Lesson". The derived
// id for "Lesson" is the renamed document's, `saveCategory` writes with
// `{ merge: true }`, and "Piano" is renamed back to "Lesson", taking every
// event filed under it.
//
// **A uniqueness-index collection** (`categoryNames/{key}`, written in the same
// batch, update allowed only by the category already holding the key — a
// strict create-once rule would fail the built-in reservations `saveCategory`
// materialises with a studio's first category) does not converge — it
// **rejects**. The losing device's category disappears, events filed under it
// offline point at nothing, and a rename that collides is refused after the
// teacher watched it take effect (#162).
//
// **The merge flow repairs instead**: `duplicateCategoryGroups` and
// `planCategoryMerge` below say which duplicates a studio holds and what
// merging one would write, and
// components/calendar/categories/MergeCategoriesPanel.jsx is the screen. MMS
// has no merge flow; with no offline editing, the state cannot arise there.
//
// **MMS refuses a duplicate** on Calendar → Options → Categories & Locations:
// Save leaves the drawer open with an inline message under the Name field,
//
//     An event category with the name Recital already exists.
//
// Save is enabled throughout — the refusal arrives on submit (#116's shape) —
// and it folds case and surrounding whitespace, quoting the existing
// category's own spelling.
//
// **Where we knowingly diverge: internal whitespace runs.** MMS accepts
// "Group  Lesson" (two spaces) beside "Group Lesson", two rows that render
// identically. `categoryKey` collapses internal runs and refuses the second.
//
// The wording is ours: MMS names the kind of thing ("An event category with
// the name X already exists"); ours names the category ("There is already a
// category called X").
export function categoryErrors(draft, categories = []) {
  const errors = {};
  // Not a check for an empty name: that greys the button out in
  // CategoriesDrawer. How MMS's Add Category treats a blank is not known.
  //
  // A Custom lead out of range (#131) is refused here, as MMS refuses it,
  // rather than clamped on blur — see `customLeadError`.
  //
  // `categories::category_errors` in the core.
  const leads = draft?.reminderLeadHours || {};
  const refused = coreSync().categoryErrors({
    id: textOrNull(draft?.id),
    label: typeof draft?.label === 'string' ? draft.label : null,
    emailLead: textOrNull(leads.email),
    smsLead: textOrNull(leads.sms),
  }, refsOf(categories));
  for (const e of refused) errors[e.field] = e.message;
  return errors;
}

// ─── Noticing the duplicate #129 cannot prevent, and merging it ─────────────
//
// #142's repair (see `categoryErrors` for why not prevention). It helps a
// studio already holding two "Lesson"s, and it is the operation #110's
// backfill wants a person to perform when it reports an `ambiguous` key.
//
// The screen and its wording are components/calendar/categories/
// MergeCategoriesPanel.jsx; these two functions are the mechanical half —
// which duplicates a studio holds and exactly what a merge would write.
//
// **Not automatic, and neither picks a winner.** Folding two categories
// together deletes one and re-files every lesson and student under it, and
// which spelling survives is the teacher's. `duplicateCategoryGroups` reports
// the members; `planCategoryMerge` has to be told which id survives — the rule
// `canonicalSpelling` in domain/tags.js and `planCategoryLink` below keep.
//
// The keys are `categoryKey`'s, so this and the editor's refusal agree about
// what "the same name" means.

// The keys this studio answers to more than once, and who answers to each.
//
// Members come back in `sortCategories` order — alphabetical with an id
// tie-break — so two devices showing one duplicate group list it identically.
//
// A category with no id or no usable key is skipped rather than grouped, as
// `indexCategoriesByLabel` skips it: it cannot be merged into or from.
export function duplicateCategoryGroups(categories = []) {
  const list = categories || [];
  return coreSync().duplicateCategoryGroups(refsOf(list))
    .map((g) => ({ key: g.key, members: g.members.map((i) => list[i]) }));
}

// What merging one duplicate group into `keepId` would write, and what it costs.
//
// `rows` is the shape `planCategoryBackfill` takes — `{ collection, id,
// categoryId, categoryLabel }` — every document that names a category.
//
// Four verdicts:
//
//   `merge`        — the group exists and `keepId` is in it. `writes` re-points
//                    every row filed under a loser at the survivor, **and
//                    rewrites `categoryLabel` to the survivor's spelling**.
//                    Both, for `planCategoryLink`'s reason.
//   `noDuplicate`  — no category answers to this key twice. Nothing to do, so
//                    a stale prompt deletes nothing.
//   `unknownKeep`  — `keepId` is not one of the group's members. **Nothing is
//                    written and no substitute is chosen**, so no category the
//                    teacher never nominated is deleted.
//   `noKey`        — the group was not named at all.
//
// ── The one consequence a caller has to be told about ─────────────────────
//
// `dropsDefaultCategory` is true when the merge deletes the document whose id
// is `DEFAULT_CATEGORY_ID`. It is reported rather than refused: either
// direction of merge is reasonable, and only the teacher knows which spelling
// they want. `defaultEventCategory` finds the default by id and falls back to
// `categories[0]`, so after such a merge every new event opens on whatever
// sorts first.
//
// Nothing here writes. It returns a plan, as `planCategoryBackfill` does.
//
// The plan is `categories::plan_category_merge` in the core; this turns its
// positions back into the categories and rows it was handed. For `unknownKeep` both candidates are reported, so a caller
// can re-offer the choice rather than having to look the group up again; and
// each write takes the survivor's spelling, not the row's (see
// `planCategoryLink`).
export function planCategoryMerge({ categories = [], rows = [], key, keepId } = {}) {
  const list = categories || [];
  const at = { key: key ?? null, keepId: keepId ?? null };
  const p = coreSync().planCategoryMerge(
    refsOf(list),
    (rows || []).map((r) => ({
      collection: textOrNull(r?.collection),
      id: textOrNull(r?.id),
      categoryId: textOrNull(r?.categoryId),
      categoryLabel: textOrNull(r?.categoryLabel),
    })),
    key ? String(key) : null,
    textOrNull(keepId),
  );
  if (p.verdict !== 'merge') {
    return {
      ...at, verdict: p.verdict, keep: null, remove: [], writes: [], counts: EMPTY_MERGE_COUNTS,
      ...(p.verdict === 'unknownKeep' ? { members: p.members.map((i) => list[i]) } : {}),
    };
  }
  const keep = list[p.keep];
  const remove = p.remove.map((i) => list[i]);
  const writes = p.writes.map((i) => ({
    collection: rows[i]?.collection ?? null,
    id: rows[i]?.id ?? null,
    categoryId: keep.id,
    categoryLabel: keep.label,
  }));
  return {
    ...at,
    verdict: 'merge',
    keep,
    remove,
    writes,
    dropsDefaultCategory: p.dropsDefaultCategory,
    counts: { remove: remove.length, writes: writes.length },
  };
}

// Named once so that the four no-op verdicts cannot drift into four shapes.
const EMPTY_MERGE_COUNTS = Object.freeze({ remove: 0, writes: 0 });

// ─── Matching a bare label back to a category, once ─────────────────────────
//
// #110. Everything below exists for a **one-off backfill** and must never
// become a read path: `relabelCategory` in data/useEvents.jsx matches on
// `categoryId` and never on a name, because rewriting a free-text string that
// happens to match is guessing at what the teacher meant. A migration may
// guess because a person runs it, reads what it proposes, and says yes.
//
// `categoryKey` above is shared with the editor's refusal and is the one thing
// here that a read path touches. Nothing else below it is.

// The studio's categories, indexed by that key, plus the keys that more than
// one category answers to.
//
// The editor refuses to *create* a second category answering to a key one
// already answers to (#129, `categoryErrors` above), but two offline devices
// can still get there, and a studio may already hold such a pair. So the
// ambiguous case is handled here: a match there is unanswerable.
export function indexCategoriesByLabel(categories = []) {
  const byKey = new Map();
  const ambiguous = new Set();
  for (const c of categories) {
    const key = categoryKey(c?.label);
    if (!key || !c?.id) continue;
    if (byKey.has(key)) ambiguous.add(key);
    else byKey.set(key, c);
  }
  for (const key of ambiguous) byKey.delete(key);
  return { byKey, ambiguous };
}

// What the backfill would do to one document, and why.
//
// Four verdicts:
//
//   `linked`     — the document already names a category by id. Left alone
//                  whatever its label says: the id is the authority, and
//                  `relabelCategory` already reaches it.
//   `match`      — exactly one category answers to this label's key. The
//                  document gets that category's `id` **and its canonical
//                  label**, so one category has one name on screen.
//   `ambiguous`  — two or more categories share this key, differing only by
//                  case or by whitespace. **Nothing is written, and neither
//                  category is chosen**; any pick would be an arbitrary and
//                  permanent choice on the teacher's behalf. The row is
//                  reported with both candidates so a person can merge or
//                  rename them and run it again.
//   `unmatched`  — no category answers to this key, which includes a document
//                  whose label is empty or missing. **Nothing is written, and
//                  no category is created**: one created from a stray label
//                  would put the free-text defect back under a fresh id, and
//                  `saveCategory` would materialise the six built-ins as a side
//                  effect. The row is reported with its label.
//
// A `match` adds an id the document did not have and normalises a label it
// already had. Re-running after fixing an ambiguity or adding a category picks
// up what was skipped and rewrites nothing that was done.
export function planCategoryLink(row, index) {
  const at = { collection: row?.collection ?? null, id: row?.id ?? null };
  if (row?.categoryId) {
    return { ...at, verdict: 'linked', categoryId: row.categoryId, label: row?.categoryLabel ?? null };
  }
  const label = row?.categoryLabel ?? null;
  const key = categoryKey(label);
  if (key && index.ambiguous.has(key)) {
    return { ...at, verdict: 'ambiguous', label, key };
  }
  const category = key ? index.byKey.get(key) : null;
  if (!category) return { ...at, verdict: 'unmatched', label, key };
  return {
    ...at,
    verdict: 'match',
    label,
    categoryId: category.id,
    // The category's spelling, not the document's.
    categoryLabel: category.label,
  };
}

// The whole plan, over every document the migration was given.
//
// Returns the writes separately from the skips: the writes are what an
// `--apply` run commits, and the skips are the report a person acts on.
export function planCategoryBackfill({ categories = [], rows = [] } = {}) {
  const index = indexCategoriesByLabel(categories);
  const plans = rows.map((row) => planCategoryLink(row, index));
  const of = (verdict) => plans.filter((p) => p.verdict === verdict);
  return {
    writes: of('match'),
    ambiguous: of('ambiguous'),
    unmatched: of('unmatched'),
    linked: of('linked'),
    // Named rather than derived at each call site: this is what a run prints.
    counts: {
      rows: plans.length,
      writes: of('match').length,
      ambiguous: of('ambiguous').length,
      unmatched: of('unmatched').length,
      linked: of('linked').length,
    },
  };
}
